import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { TutorAttendanceRatesIndexData } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { useAdminRoutes } from '@shared/lib/routes'
import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { getTutorAttendanceRatesFilterParams } from '../lib/tutor-attendance-rates-query'
import { useTutorAttendanceRatesStore } from '../model/store'

interface TutorAttendanceRatesApiOptions {
  route?: RouteLocationNormalizedGeneric
}

/** Filters and pages change faster than the index answers, so only the latest request may land. */
let activeRequest: AbortController | null = null

export const abortTutorAttendanceRatesApi = (): void => {
  activeRequest?.abort()
  activeRequest = null
  useTutorAttendanceRatesStore().setLoading(false)
}

/**
 * `GET /admin-api/tutor-attendance-rates` — the index. Returns `{ rates, totals, meta }`, or `null` when
 * failed or superseded.
 */
export const useTutorAttendanceRatesApi = async (
  options?: TutorAttendanceRatesApiOptions,
): Promise<TutorAttendanceRatesIndexData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useTutorAttendanceRatesStore()

  const url = useAdminRoutes('admin.tutor-attendance-rates.data', pick({
    ...getTutorAttendanceRatesFilterParams(store.getFilters),
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  activeRequest?.abort()
  const request = new AbortController()
  activeRequest = request

  store.setLoading(true)

  try {
    if (!url) {
      toast.error('Unexpected error. Invalid request URL.')
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url, { signal: request.signal })

    if (request.signal.aborted) return null

    if (!data.success) {
      toast.error(data.message)
      return null
    }

    const indexData: TutorAttendanceRatesIndexData | null = data.data || null

    if (indexData) {
      store.setRates(indexData.rates)
      store.setTotals(indexData.totals)
      store.setMeta(indexData.meta)
    }

    return indexData
  } catch (err: unknown) {
    if (request.signal.aborted) return null

    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    if (activeRequest === request) {
      activeRequest = null
      store.setLoading(false)
    }
  }
}
